import { AppleLogo, GooglePlayLogo, Sparkle } from '@phosphor-icons/react';
import { List, Map as MapIcon, X, ArrowUp, ArrowRight, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import AiSearchForm from './AiSearchForm';
import AiSearchMap from './AiSearchMap';
import AiSearchResults from './AiSearchResults';
import AiSearchClarification from './AiSearchClarification';
import AudioTourUpsell from './AudioTourUpsell';
import MyTripsLauncher from './MyTripsLauncher';
import RefinementHistoryAccordion from './RefinementHistoryAccordion';
import SaveTripBar from './SaveTripBar';
import TripsPlannedCounter from './TripsPlannedCounter';
import TrendingTripsSheet from './TrendingTripsSheet';
import useAiSearch from './useAiSearch';

import { getBackendUrl } from '../../lib/runtimeConfig';

const API = `${getBackendUrl()}/api`;

/**
 * Inline trending-now footer used as the bottom slab of the unified
 * ActivePlannerLayout card. Visually it's a dark gold accent strip that
 * sits flush against the white sections above, giving the card a
 * premium "magazine-style" footer treatment. Functionally identical to
 * <TripsPlannedCounter /> (same /api/ai-search/stats poll + opens the
 * same TrendingTripsSheet) — but styled to live inside a parent card
 * with no margin / radius of its own.
 */
const TrendingNowFooter = () => {
  const [count, setCount] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/ai-search/stats`, { timeout: 8000 });
        if (cancelled) return;
        // Prefer new `total_trips` field; fall back to legacy `trips_this_month`
        // so older backend deployments still render a number.
        const n = res?.data?.total_trips ?? res?.data?.trips_this_month;
        if (typeof n === 'number') setCount(n);
      } catch { /* non-critical */ }
    };
    fetchStats();
    const t = setInterval(fetchStats, 45000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Always render — even at 0 trips — so the section is never missing.
  const safeCount = typeof count === 'number' ? count : 0;
  const hasTrips = safeCount > 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setSheetOpen(true)}
        className="group/trending block w-full text-left bg-gradient-to-br from-[#1a1a2e] via-[#241a35] to-[#1a1a2e] hover:from-[#241a35] hover:via-[#2d1f43] hover:to-[#241a35] border-t border-gold-500/30 transition-all"
        data-testid="trips-planned-counter"
        aria-live="polite"
        title="Browse the community trending feed"
      >
        <div className="flex items-center gap-3 sm:gap-4 px-5 sm:px-6 py-4 sm:py-4">
          {/* Upvote-style chip on the left */}
          <div className="shrink-0 inline-flex flex-col items-center justify-center w-11 h-11 sm:w-12 sm:h-12 rounded-lg bg-gold-500/20 border border-gold-400/50 group-hover/trending:bg-gold-500/30 transition-colors">
            <ArrowUp className="w-4 h-4 sm:w-5 sm:h-5 text-gold-300" strokeWidth={2.5} />
            <span className="text-[9px] sm:text-[10px] font-bold text-gold-200 leading-none tabular-nums">
              {hasTrips ? safeCount.toLocaleString('en-US') : 'NEW'}
            </span>
          </div>

          {/* Middle copy */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.18em] text-gold-300 mb-0.5">
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500" />
              </span>
              <TrendingUp className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              Top 10 · Trending trips
            </div>
            <div className="text-sm sm:text-base font-semibold text-white leading-tight">
              {hasTrips ? (
                <>
                  <span className="text-gold-200">{safeCount.toLocaleString('en-US')}</span> trip{safeCount === 1 ? '' : 's'} planned by the community
                </>
              ) : (
                <>Be the <span className="text-gold-200">first</span> to plan a trip</>
              )}
            </div>
            <div className="text-[11px] sm:text-xs text-white/60 mt-0.5">
              {hasTrips ? 'Vote for the ones you love — top voted rise to the top →' : 'Yours will show up here for everyone to see →'}
            </div>
          </div>

          {/* Right arrow CTA */}
          <div className="shrink-0 inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-gold-300 group-hover/trending:text-gold-200 transition-colors">
            <span className="hidden sm:inline">See top 10</span>
            <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover/trending:translate-x-0.5" />
          </div>
        </div>
      </button>
      <TrendingTripsSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </>
  );
};

/**
 * Compact card containing the AI search input, active-thread indicator,
 * recovery panel, and (when results exist) the scrolling itinerary stack.
 * Used in BOTH layout modes so the user never sees the search wrapper disappear.
 */
const PlannerCard = ({ search, compact = false }) => {
  const showResults = compact && (search.isSearching || search.results.length > 0 || Boolean(search.clarification));
  const showSaveBar = compact && !search.isSearching && search.results.length > 0;
  return (
    <div
      className={`relative bg-white rounded-2xl shadow-[0_25px_60px_-15px_rgba(0,0,0,0.5)] ring-1 ring-black/5 ${
        compact ? 'p-5 sm:p-6 h-full flex flex-col' : 'p-5 sm:p-7'
      }`}
      data-testid="hero-itinerary-card"
    >
      {/* X-close — only in split-screen mode. Closes panel, KEEPS chat history. */}
      {compact && (
        <button
          type="button"
          onClick={search.closePanel}
          aria-label="Close trip plan panel"
          className="absolute top-3 right-3 inline-flex items-center justify-center w-8 h-8 rounded-full text-gray-400 hover:text-[#1a1a2e] hover:bg-gray-100 transition-colors z-10"
          data-testid="planner-card-close"
        >
          <X className="w-4 h-4" />
        </button>
      )}

      <div className="mb-1 shrink-0 pr-8">
        <div className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.18em] text-wine-600 mb-2">
          <Sparkle size={14} weight="fill" className="text-gold-500" />
          <span>AI Trip Planner Concierge · New</span>
        </div>
        <h2 className="font-serif text-2xl sm:text-3xl text-[#1a1a2e] leading-tight">
          Plan Your Dream Trip
        </h2>
        <p className="text-gray-500 text-xs sm:text-sm mt-1 mb-5 leading-relaxed">
          Describe the vibe — region, mood, amenities — and your AI Concierge builds you a 3-stop wine country trip plan in seconds.
        </p>
      </div>

      <div className="shrink-0">
        <AiSearchForm {...search} />
      </div>

      {/* Scrolling results stack — only visible in compact (split-screen) mode while active. */}
      {showResults && (
        <div
          className="mt-4 pt-4 border-t border-gray-100 flex-1 overflow-y-auto -mr-2 pr-2"
          data-testid="hero-itinerary-results-scroller"
        >
          {/* Clarification card — backend qualifier intercepted an
              ambiguous query. Render it above (and instead of) results. */}
          {search.clarification && (
            <div className="mb-3">
              <AiSearchClarification
                clarification={search.clarification}
                onResolve={search.resolveClarification}
                onCancel={() => search.setClarification(null)}
              />
            </div>
          )}
          <AiSearchResults
            isSearching={search.isSearching}
            results={search.results}
            submittedQuery={search.submittedQuery}
            commentary={search.commentary}
            followUps={search.followUps}
            matchedWinery={search.matchedWinery}
            route={search.route}
            radiusSpread={search.radiusSpread}
            onRefine={(prompt) => {
              search.setQuery(prompt);
              search.runSearch(prompt);
            }}
          />
        </div>
      )}

      {/* Refinement history accordion — appears below the follow-up
          chips, above the Save & Share bar (the rectangle area the user
          highlighted). Shows from turn 1 onward so users can always go
          back and inspect prior turns + the stops the AI suggested for
          each one without leaving the live conversation. */}
      {showSaveBar && search.activeChatHistory && search.activeChatHistory.length > 0 && (
        <div className="mt-3 shrink-0">
          <RefinementHistoryAccordion
            chatHistory={search.activeChatHistory}
            showFinalRow={false}
          />
        </div>
      )}

      {/* Save + My-trips bar — appears once results are ready */}
      {showSaveBar && (
        <div className="mt-4 pt-4 border-t border-gray-100 shrink-0">
          <SaveTripBar
            submittedQuery={search.submittedQuery}
            results={search.results}
            chatHistory={search.activeChatHistory}
          />
        </div>
      )}

      {/* Audio Tour upsell — surfaces region-relevant tours under the
          Save & Share bar so the user can extend the trip with a
          sommelier-narrated audio experience in one tap. */}
      {showSaveBar && (
        <div className="mt-3 shrink-0">
          <AudioTourUpsell
            submittedQuery={search.submittedQuery}
            results={search.results}
          />
        </div>
      )}

      <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-end gap-2 text-[10px] sm:text-[11px] text-gray-400 shrink-0">
        <div className="flex items-center gap-3">
          {!compact && <MyTripsLauncher />}
          <span className="inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
            Live
          </span>
        </div>
      </div>
    </div>
  );
};

/**
 * ActivePlannerLayout — the layout shown after the user kicks off the
 * AI Concierge conversation.
 *
 * v2 (Feb 2026) — driven by user feedback that the previous single-card
 * panel felt crammed:
 *   - Left column (60%): the *output* — switchable between a clean,
 *     scrollable List of the proposed stops (default) and the embedded
 *     Map view. An elegant pill toggle lets the user flip without
 *     losing context. Below the output sits the refinement-history
 *     accordion + the Save & Share bar. Bigger fonts here because we
 *     have the room.
 *   - Right column (40%): the *input* — just the AI Concierge input,
 *     follow-up suggestion chips/typewriter, and the radius slider.
 *     Nothing else. Keeps the active conversation surface focused.
 *
 * Why a dedicated component (instead of conditionals inside PlannerCard):
 * the active layout shares almost nothing with the initial planner card
 * — different DOM tree, different concerns. Splitting keeps PlannerCard
 * uncomplicated for first-touch UX and lets this layout iterate freely.
 */
const ActivePlannerLayout = ({ search }) => {
  const [view, setView] = useState('list'); // 'list' | 'map'
  const layoutRef = useRef(null);
  const showSaveBar = !search.isSearching && search.results.length > 0;

  // Anchor scroll position when a follow-up suggestion is clicked. Without
  // this, the click bubbles, the page re-renders the (taller) list of new
  // stops, and the browser leaves the user mid-page — scrolling them past
  // the planner so they have to scroll back up to see the new itinerary.
  // We capture the layout's top edge BEFORE the refine fires, then restore
  // it after React commits. `scrollIntoView({block: 'start'})` keeps the
  // planner banner pinned to the viewport top through the transition.
  const onRefine = (prompt) => {
    search.setQuery(prompt);
    search.runSearch(prompt);
    requestAnimationFrame(() => {
      layoutRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  return (
    <div
      ref={layoutRef}
      className="flex flex-col lg:flex-row gap-6 lg:gap-8 items-start animate-fade-in scroll-mt-24"
      data-testid="hero-split-active"
    >
      {/* ============ LEFT COLUMN — OUTPUT (results / map) ============
          Content-driven height — the page scrolls naturally when the
          accordion expands or the list grows. The earlier fixed-height
          container hid Save & Share + My Trips off-screen when content
          overflowed. */}
      <div className="w-full lg:basis-3/5 lg:max-w-[60%] flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* List / Map toggle pill — sticky at the top of the card so it's
            always reachable even while the user scrolls the long list. */}
        <div className="sticky top-0 z-10 bg-white px-4 sm:px-5 pt-4 pb-3 border-b border-gray-100 flex items-center justify-between gap-2 sm:gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">
              Trip Plan for
            </div>
            <div
              className="text-sm sm:text-base font-semibold text-[#1a1a2e] line-clamp-2 sm:truncate leading-snug"
              title={search.submittedQuery || ''}
              data-testid="ai-search-submitted-query"
            >
              {search.submittedQuery || 'Your wine country trip'}
            </div>
          </div>
          <div
            role="tablist"
            aria-label="Result view"
            className="inline-flex items-center rounded-full bg-gray-100 p-0.5 shrink-0"
            data-testid="result-view-toggle"
          >
            <button
              type="button"
              role="tab"
              aria-selected={view === 'list'}
              onClick={() => setView('list')}
              className={`inline-flex items-center gap-1 sm:gap-1.5 text-[11px] sm:text-[12px] font-semibold px-2 sm:px-3 py-1.5 rounded-full transition-colors ${
                view === 'list' ? 'bg-white text-wine-700 shadow-sm' : 'text-gray-500 hover:text-[#1a1a2e]'
              }`}
              data-testid="result-view-list"
              aria-label="List view"
            >
              <List className="w-3.5 h-3.5" /> List
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'map'}
              onClick={() => setView('map')}
              className={`inline-flex items-center gap-1 sm:gap-1.5 text-[11px] sm:text-[12px] font-semibold px-2 sm:px-3 py-1.5 rounded-full transition-colors ${
                view === 'map' ? 'bg-white text-wine-700 shadow-sm' : 'text-gray-500 hover:text-[#1a1a2e]'
              }`}
              data-testid="result-view-map"
              aria-label="Map view"
            >
              {/* Drop the "View" word on mobile — the icon + word "Map"
                  is unambiguous and the saved horizontal space lets the
                  title above breathe instead of truncating mid-word. */}
              <MapIcon className="w-3.5 h-3.5" />
              <span>Map</span>
              <span className="hidden sm:inline">&nbsp;View</span>
            </button>
          </div>
        </div>

        {/* Content area — list flows naturally; map gets a fixed height so
            it doesn't push subsequent content miles down the page. */}
        <div>
          {view === 'list' ? (
            <div className="px-4 sm:px-5 py-4" data-testid="result-view-list-pane">
              {search.clarification && (
                <div className="mb-3">
                  <AiSearchClarification
                    clarification={search.clarification}
                    onResolve={search.resolveClarification}
                    onCancel={() => search.setClarification(null)}
                  />
                </div>
              )}
              <AiSearchResults
                isSearching={search.isSearching}
                results={search.results}
                submittedQuery={null}
                commentary={search.commentary}
                followUps={search.followUps}
                matchedWinery={search.matchedWinery}
                route={search.route}
                radiusSpread={search.radiusSpread}
                onRefine={onRefine}
                hideFollowUps
              />
            </div>
          ) : (
            <div className="h-[55vh] min-h-[400px]" data-testid="result-view-map-pane">
              <AiSearchMap mapped={search.mapped} route={search.route} routeError={search.routeError} />
            </div>
          )}
        </div>

        {/* Below-the-fold actions: accordion + save & share — always
            visible at the bottom of the column. Page scroll lets the user
            reach them regardless of how long the accordion + list grow. */}
        {showSaveBar && (
          <div className="border-t border-gray-100 px-4 sm:px-5 py-3 space-y-3 bg-gray-50/40">
            {search.activeChatHistory && search.activeChatHistory.length > 0 && (
              <RefinementHistoryAccordion
                chatHistory={search.activeChatHistory}
                showFinalRow={false}
              />
            )}
            <SaveTripBar
              submittedQuery={search.submittedQuery}
              results={search.results}
              chatHistory={search.activeChatHistory}
            />
            <AudioTourUpsell
              submittedQuery={search.submittedQuery}
              results={search.results}
            />
          </div>
        )}
      </div>

      {/* ============ RIGHT COLUMN — UNIFIED PLANNER CARD ============
          One single card containing: (1) AI Concierge input, (2) Keep
          Refining grid, (3) Trending Now footer. Previously these rendered
          as three separate cards with vertical gaps between them which
          visually fragmented the experience — users perceived them as
          unrelated widgets. Now they share one shadow + one rounded
          container with internal hairline dividers, so the eye follows
          input → refine → discover as one continuous flow.

          Sticky on desktop so the card stays in view while the user scrolls
          the left output. `overflow-hidden` clips the trending footer's
          rounded edges into the card's bottom corners. */}
      <div className="w-full lg:basis-2/5 lg:max-w-[40%] lg:sticky lg:top-24">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden" data-testid="unified-planner-card">
          {/* ---- Section 1: AI Concierge input ---- */}
          <div className="p-5 sm:p-7 flex flex-col gap-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-wine-600 mb-1">
                  <Sparkle size={12} weight="fill" className="text-gold-500" />
                  <span>AI Trip Planner Concierge</span>
                </div>
                <h2 className="font-serif text-xl sm:text-2xl text-[#1a1a2e] leading-tight">
                  Plan Your Dream Trip
                </h2>
                <p className="text-gray-500 text-xs sm:text-sm mt-1 leading-relaxed">
                  Describe the vibe – region, mood, amenities – and your AI Concierge builds you a 3-stop wine country trip plan in seconds.
                </p>
              </div>
              <button
                type="button"
                onClick={search.closePanel}
                aria-label="Close trip plan panel"
                className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full text-gray-400 hover:text-[#1a1a2e] hover:bg-gray-100 transition-colors"
                data-testid="planner-card-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <AiSearchForm {...search} />
          </div>

          {/* ---- Section 2: Keep refining grid ---- */}
          {!search.isSearching && search.results.length > 0 && search.followUps?.length > 0 && (
            <div
              className="border-t border-gray-100 bg-gradient-to-b from-gray-50/60 to-white px-5 sm:px-7 py-4 sm:py-5"
              data-testid="ai-followup-grid"
            >
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-wine-600 mb-3 flex items-center gap-1.5">
                <Sparkle size={12} weight="fill" className="text-gold-500" />
                Keep refining — tap any
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {search.followUps.slice(0, 4).map((s, i) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => onRefine(s)}
                    className="text-left text-xs sm:text-sm text-[#1a1a2e] bg-white hover:bg-wine-600 hover:text-white border border-gray-200 hover:border-wine-600 rounded-xl px-3.5 py-3 transition-all hover:-translate-y-0.5 hover:shadow-md leading-snug min-h-[3.5rem] flex items-center"
                    data-testid={`ai-followup-grid-${i}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ---- Section 3: Trending Now footer ----
              Inlined here (instead of using <TripsPlannedCounter />) so it
              becomes the rounded bottom edge of the unified card with a
              dark gold treatment that visually weighs the eye to the
              community-discovery CTA. Same data shape + behaviour as the
              standalone component used in the initial PlannerCard. */}
          <TrendingNowFooter />
        </div>
      </div>
    </div>
  );
};

const HeroSection = ({ conciergeOnly = false }) => {
  const search = useAiSearch();
  const { searchActive } = search;
  const sectionRef = useRef(null);

  // On mount, give the planner input focus so visitors can start typing
  // immediately. Important especially on the dedicated /concierge route
  // where the planner IS the whole page — anything else would feel inert.
  useEffect(() => {
    const id = window.setTimeout(() => {
      const input = document.querySelector('[data-testid="ai-search-input"]');
      input?.focus({ preventScroll: true });
    }, 80);
    return () => window.clearTimeout(id);
  }, []);

  // On the dedicated /concierge route the marketing-left-column layout
  // would look hollow ("Wine Tasting is Now Social" doesn't belong on a
  // page named "Concierge Trip Planner"). When conciergeOnly is true and
  // there's no active search yet, we render JUST the planner card centred
  // on the page; once the user starts a search we switch to the same
  // split-screen layout as the home page.
  const showMarketingColumn = !conciergeOnly;

  return (
    <section
      ref={sectionRef}
      className="relative flex items-center overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      }}
      data-testid="hero-section"
    >
      <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent" />
      <div className="absolute top-20 left-10 w-72 h-72 bg-wine-600/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-gold-500/10 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 sm:pt-20 pb-12 sm:pb-16 w-full relative z-10">
        {searchActive ? (
          // ===== ACTIVE LAYOUT (v2 — user-driven redesign) =====
          //   LEFT  (60%): results display with List ↔ Map toggle,
          //                  followed by accordion + Save & Share bar
          //   RIGHT (40%): compact AI Concierge input bar only
          //                  (title, follow-up input, radius slider,
          //                   reset). Everything heavy moved to the left
          //                  so the right column never feels crammed.
          <ActivePlannerLayout search={search} />
        ) : !showMarketingColumn ? (
          // ===== /concierge ROUTE: planner ONLY, centred, full viewport =====
          <div className="flex flex-col items-center justify-center min-h-[70vh] animate-fade-in" data-testid="hero-concierge-only">
            <div className="w-full max-w-2xl">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-wine-500/30 via-gold-400/20 to-gold-500/30 blur-3xl rounded-3xl" />
                <div className="relative">
                  <PlannerCard search={search} />
                </div>
              </div>
              <TripsPlannedCounter />
            </div>
          </div>
        ) : (
          // ===== DEFAULT HERO: heading + pills/buttons on the left, planner card on the right =====
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-14">
            {/* LEFT: hero text with generous spacing; pills sit tight on top of the download buttons. */}
            <div
              className="w-full lg:flex-1 text-left min-w-0 animate-fade-in"
              data-testid="hero-left-column"
            >
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 mb-6 sm:mb-8">
                <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-400 rounded-full animate-pulse" />
                <span className="text-white/90 text-xs sm:text-sm font-medium">#1 Downloaded Wine App</span>
              </div>

              <h1 className="font-serif text-3xl sm:text-5xl md:text-6xl lg:text-7xl text-white mb-6 sm:mb-8 leading-[1.1]">
                Wine Tasting<br />
                is Now <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-400 via-gold-300 to-gold-400 italic">Social</span>
              </h1>

              <p className="text-white/70 text-base sm:text-lg md:text-xl leading-relaxed mb-10 sm:mb-12 max-w-lg">
                Share your wine tasting adventures via Photos & Check-ins. Found a great winery? Snap a selfie and instantly share with everyone!
              </p>

              {/* Pills — tight to the buttons below */}
              <div className="flex flex-wrap gap-2 sm:gap-3 mb-3 sm:mb-4">
                {['140+ Wine Deals', 'GPS Enabled', 'Free Download'].map((feature) => (
                  <span
                    key={feature}
                    className="bg-white/5 border border-white/10 text-white/80 text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-full"
                    data-testid={`hero-feature-pill-${feature.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
                  >
                    {feature}
                  </span>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <a
                  href="https://apps.apple.com/us/app/napa-valley-winery-finder-real/id419570092"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center justify-center gap-2 sm:gap-3 bg-white text-gray-900 font-semibold px-4 sm:px-6 py-3 sm:py-4 rounded-xl hover:bg-white/90 transition-all hover:scale-[1.02] active:scale-[0.98]"
                  data-testid="hero-app-store"
                >
                  <AppleLogo size={20} weight="fill" className="sm:w-6 sm:h-6" />
                  <div className="text-left">
                    <div className="text-xs sm:text-xs text-gray-500 uppercase tracking-wide">Download on</div>
                    <div className="text-xs sm:text-sm font-bold">App Store</div>
                  </div>
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.shoutem.n75202&hl=en"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center justify-center gap-2 sm:gap-3 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold px-4 sm:px-6 py-3 sm:py-4 rounded-xl hover:bg-white/20 transition-all hover:scale-[1.02] active:scale-[0.98]"
                  data-testid="hero-play-store"
                >
                  <GooglePlayLogo size={20} weight="fill" className="sm:w-6 sm:h-6" />
                  <div className="text-left">
                    <div className="text-xs sm:text-xs text-white/60 uppercase tracking-wide">Get it on</div>
                    <div className="text-xs sm:text-sm font-bold">Google Play</div>
                  </div>
                </a>
              </div>
            </div>

            {/* RIGHT: planner card */}
            <div className="w-full lg:flex-1 lg:max-w-xl animate-fade-in" data-testid="hero-right-column">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-wine-500/30 via-gold-400/20 to-gold-500/30 blur-3xl rounded-3xl" />
                <div className="relative">
                  <PlannerCard search={search} />
                </div>
              </div>
              <TripsPlannedCounter />
            </div>
          </div>
        )}
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent pointer-events-none" />
    </section>
  );
};

export default HeroSection;
